import React, { useState } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';

function MileageReport({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const FL_MILES = 450; // Length of Florida in miles
  const MAX_JUMP = 100000; // Max valid odometer change

  const runReport = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    setLoading(true);
    setReportData([]);

    try {
      // Fix date parsing to use local timezone
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T23:59:59');

      console.log('=== MILEAGE REPORT DEBUG START ===');
      console.log('Date Range:', { startDate, endDate, start, end });

      // Query daily_truck_checks table
      const { data, error } = await supabase
        .from('daily_truck_checks')
        .select('*')
        .order('created_at', { ascending: true });

      console.log('Supabase Query Result:', {
        recordCount: data?.length || 0,
        error: error,
        sampleRecord: data?.[0]
      });

      if (error) {
        console.error('Supabase Error Details:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.warn('No records found in daily_truck_checks table');
        alert('No daily truck check records found. Please ensure truck checks have been logged.');
        setLoading(false);
        return;
      }

      // Group by ambulance
      const groupedByAmbulance = {};

      (data || []).forEach((record, index) => {
        const ambulance = record.ambulance || record.location || 'Unknown';
        const odometerStr = record.odometer || record.odometer_reading || '';
        const odometer = parseFloat(String(odometerStr).replace(/[^0-9.-]+/g, '')) || 0;
        const recordDate = new Date(record.created_at);

        // Log first 3 records for debugging
        if (index < 3) {
          console.log(`Record ${index + 1}:`, {
            ambulance,
            odometerStr,
            odometer,
            recordDate: recordDate.toISOString(),
            rawRecord: record
          });
        }

        if (!isNaN(odometer) && odometer > 0 && !isNaN(recordDate.getTime())) {
          if (!groupedByAmbulance[ambulance]) {
            groupedByAmbulance[ambulance] = [];
          }
          groupedByAmbulance[ambulance].push({
            odometer,
            date: recordDate,
            rawDate: recordDate.toLocaleDateString('en-US')
          });
        } else {
          console.warn(`Skipping record ${index + 1} - Invalid data:`, {
            ambulance,
            odometer,
            recordDate: recordDate.toString()
          });
        }
      });

      console.log('Grouped Data:', {
        ambulances: Object.keys(groupedByAmbulance),
        recordCounts: Object.keys(groupedByAmbulance).map(amb => ({
          ambulance: amb,
          count: groupedByAmbulance[amb].length
        }))
      });

      const results = [];

      Object.keys(groupedByAmbulance).forEach(ambulance => {
        const records = groupedByAmbulance[ambulance];
        records.sort((a, b) => a.date - b.date);

        console.log(`Processing ${ambulance}:`, {
          totalRecords: records.length,
          dateRange: records.length > 0 ? {
            first: records[0].rawDate,
            last: records[records.length - 1].rawDate
          } : 'No records'
        });

        // Find start record (first on/after start date)
        let startRecord = null;
        for (const rec of records) {
          if (rec.date >= start) {
            startRecord = rec;
            break;
          }
        }

        // Find end record (last on/before end date)
        let endRecord = null;
        for (let i = records.length - 1; i >= 0; i--) {
          if (records[i].date <= end) {
            endRecord = records[i];
            break;
          }
        }

        console.log(`${ambulance} - Found records:`, {
          startRecord: startRecord ? {
            date: startRecord.rawDate,
            odometer: startRecord.odometer
          } : 'NOT FOUND',
          endRecord: endRecord ? {
            date: endRecord.rawDate,
            odometer: endRecord.odometer
          } : 'NOT FOUND'
        });

        if (startRecord && endRecord && endRecord.date > startRecord.date) {
          const miles = endRecord.odometer - startRecord.odometer;

          console.log(`${ambulance} - Calculating miles:`, {
            start: startRecord.odometer,
            end: endRecord.odometer,
            miles,
            valid: miles >= 0 && miles <= MAX_JUMP
          });

          // Validate mileage
          if (miles >= 0 && miles <= MAX_JUMP) {
            const floridaTrips = (miles / FL_MILES).toFixed(1);

            results.push({
              ambulance,
              startDateUsed: startRecord.rawDate,
              endDateUsed: endRecord.rawDate,
              startOdometer: startRecord.odometer,
              endOdometer: endRecord.odometer,
              milesDriven: miles,
              floridaTrips
            });
          } else {
            console.warn(`${ambulance} - Mileage validation failed:`, {
              miles,
              MAX_JUMP,
              reason: miles < 0 ? 'Negative miles' : 'Exceeds max jump'
            });
          }
        } else {
          console.warn(`${ambulance} - Skipped:`, {
            reason: !startRecord ? 'No start record in range' :
                    !endRecord ? 'No end record in range' :
                    'End date not after start date'
          });
        }
      });

      console.log('Final Results:', results);
      console.log('=== MILEAGE REPORT DEBUG END ===');

      setReportData(results);

      if (results.length === 0) {
        alert('No mileage data found for the selected date range. Check console for detailed logs.');
      }
    } catch (error) {
      console.error('!!! MILEAGE REPORT ERROR !!!');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Full error object:', error);
      alert(`Error running report: ${error.message}\n\nCheck browser console (F12) for detailed logs.`);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });

    const sorted = [...reportData].sort((a, b) => {
      if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
      if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setReportData(sorted);
  };

  const exportToCSV = () => {
    if (reportData.length === 0) {
      alert('No data to export.');
      return;
    }

    const headers = ['Ambulance', 'Start Date', 'End Date', 'Start Odometer', 'End Odometer', 'Miles Driven', 'Florida Trips'];
    const csvData = [
      headers.join(','),
      ...reportData.map(row =>
        [
          row.ambulance,
          row.startDateUsed,
          row.endDateUsed,
          row.startOdometer,
          row.endOdometer,
          row.milesDriven,
          row.floridaTrips
        ]
          .map(field => `"${field}"`)
          .join(',')
      )
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `mileage_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Admin check at the end (after hooks)
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have administrator privileges.</p>
          <button onClick={() => onNavigate('home')} className="btn btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <HamburgerMenu isAdmin={isAdmin} onLogout={onLogout} onNavigate={onNavigate} />

      <div style={{ padding: '80px 20px 20px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Back Button */}
        <button
          onClick={() => onNavigate('admin-dashboard')}
          style={{
            padding: '8px 16px',
            border: '2px solid #0E3293',
            borderRadius: '6px',
            background: 'white',
            color: '#0E3293',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          ← Back to Admin
        </button>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0E3293 0%, #1A4DBB 100%)',
          padding: '30px',
          borderRadius: '12px',
          marginBottom: '30px',
          color: '#fff',
          boxShadow: '0 4px 12px rgba(14, 50, 147, 0.15)'
        }}>
          <h1 style={{ margin: 0, fontSize: '28px' }}>Ambulance Mileage Report</h1>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
            Track ambulance mileage with start and end odometer readings for a selected date range
          </p>
        </div>

        {/* Filters */}
        <div className="report-filters" style={{
          background: '#fff',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                Start Date:
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' }}>
                End Date:
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <button
              onClick={runReport}
              className="btn btn-primary"
              style={{
                padding: '10px 24px',
                background: '#0E3293',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Run Report
            </button>

            <button
              onClick={exportToCSV}
              disabled={reportData.length === 0}
              className="btn btn-secondary"
              style={{
                padding: '10px 24px',
                background: reportData.length === 0 ? '#ccc' : '#6c757d',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: reportData.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#0E3293', fontSize: '18px' }}>
            Report is Running...
          </div>
        )}

        {/* Table */}
        {!loading && reportData.length > 0 && (
          <>
            <div className="report-table-container">
              <div className="table-scroll">
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: '#fff',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <thead>
                    <tr style={{ background: '#0E3293', color: '#fff' }}>
                      {[
                        { key: 'ambulance', label: 'Ambulance' },
                        { key: 'startDateUsed', label: 'Start Date' },
                        { key: 'endDateUsed', label: 'End Date' },
                        { key: 'startOdometer', label: 'Start Odometer' },
                        { key: 'endOdometer', label: 'End Odometer' },
                        { key: 'milesDriven', label: 'Miles Driven' }
                      ].map(col => (
                        <th
                          key={col.key}
                          onClick={() => handleSort(col.key)}
                          style={{
                            padding: '15px',
                            textAlign: 'left',
                            fontWeight: '600',
                            cursor: 'pointer',
                            minWidth: '120px',
                            userSelect: 'none'
                          }}
                        >
                          {col.label} {sortConfig.key === col.key && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((row, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <td style={{ padding: '12px 15px' }}>{row.ambulance}</td>
                        <td style={{ padding: '12px 15px' }}>{row.startDateUsed}</td>
                        <td style={{ padding: '12px 15px' }}>{row.endDateUsed}</td>
                        <td style={{ padding: '12px 15px' }}>{row.startOdometer.toLocaleString()}</td>
                        <td style={{ padding: '12px 15px' }}>{row.endOdometer.toLocaleString()}</td>
                        <td style={{ padding: '12px 15px', fontWeight: '600', color: '#0E3293' }}>
                          {row.milesDriven.toLocaleString()} mi
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Fun Summary */}
            <div style={{
              background: '#e3f2fd',
              padding: '25px',
              borderRadius: '8px',
              marginTop: '20px',
              border: '2px solid #0E3293'
            }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#0E3293', fontSize: '20px' }}>Florida Trip Comparison 🌴</h3>
              {reportData.map((row, index) => (
                <div key={index} style={{ marginBottom: '10px', fontSize: '16px', color: '#333', lineHeight: '1.6' }}>
                  <strong>{row.ambulance}</strong> could've driven down Florida <strong style={{ color: '#0E3293' }}>{row.floridaTrips}</strong> times 🚐
                </div>
              ))}
              <div style={{ marginTop: '15px', fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                (Florida is approximately {FL_MILES} miles long from north to south)
              </div>
            </div>
          </>
        )}

        {/* Empty State */}
        {!loading && reportData.length === 0 && startDate && endDate && (
          <div style={{
            background: '#fff',
            padding: '40px',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#666',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            No valid mileage records found for the selected date range.
          </div>
        )}
      </div>

      <footer className="footer">
        <p className="text-small">
          Mileage Report - NarcoticTrack © 2025 NarcoticTrack Inventory System, LLC
        </p>
      </footer>
    </div>
  );
}

export default MileageReport;
